<template>
  <div>
    <div v-if="isLoading" class="container content center-loading">
      <logo-loading />
    </div>
    <transition name="fade">
      <div v-if="!isLoading">
        <div class="container content">
          <template v-if="isEmpty">
            <div class="cart__empty">
              <p>{{ $t('cart.empty') }}</p>
              <p>
                <nuxt-link to="/" class="btn btn--large">
                  {{ $t('cart.keep_buying') }}
                </nuxt-link>
              </p>
            </div>
          </template>
          <template v-else>
            <cart-gamification
              :is-promotion="useNewCart ? isBuyTwoGetFour : false"
              class="only-mobile"
            />

            <div class="cart__header">
              <h1 class="cart__title">{{ $t('cart.your_cart') }}</h1>
              <p class="cart__subtitle">
                {{ $t('cart.not_finish') }}
                <nuxt-link to="/" class="inline-link continue">
                  {{ $t('cart.keep_buying') }}
                </nuxt-link>
              </p>
            </div>

            <div v-if="lineItems" class="grid cart__order">
              <div
                class="grid__column grid__column--8 grid__column--rightpad-biggest cart__order-first"
              >
                <transition name="fade">
                  <div
                    v-show="showTravelCaseWarning"
                    class="travelcase-warning"
                  >
                    <div class="travelcase-warning__title">
                      <icon-warning
                        :width="$device.isDesktop ? 40 : 50"
                        :height="$device.isDesktop ? 24 : 30"
                      />
                      <b>{{ $t('travelcase_warning.title') }}</b>
                    </div>
                    <p class="travelcase-warning__description">
                      {{ $t('travelcase_warning.description') }}
                      <a
                        href="https://goca.se/whatsapp"
                        target="_blank"
                        rel="nofollow"
                        class="support-link"
                        >{{ $t('travelcase_warning.link') }}</a
                      >.
                    </p>
                  </div>
                </transition>

                <div>
                  <div
                    v-show="showCheckboxes && checkboxLoading"
                    class="cart__loading"
                  >
                    <three-dots-loading />
                  </div>
                  <transition name="fade">
                    <new-cross-sell
                      v-show="showCheckboxes && !checkboxLoading"
                      :order-number="number"
                      :mode="'cart'"
                      :label="$t('cross_sell.cart_recommendation')"
                      @updateCart="updateCart"
                      @showcheckboxes="showCheckboxesHandler"
                    />
                  </transition>
                </div>

                <line-item
                  v-for="(item, index) in lineItems"
                  :key="index"
                  :line-item="item"
                  :show-promotion="true"
                  :show-quantity="false"
                  :show-select-quantity="item.in_stock && !item.gift"
                  :show-remove="!item.gift"
                  :show-actions="true"
                  :order-number="cartNumber"
                  @updateCart="updateCart"
                  @updatedLineItem="updatedLineItem"
                />
              </div>
              <div
                class="grid__column grid__column--4 grid__column--right grid__column--leftpad-biggest cart__order-last"
              >
                <cart-gamification v-if="!useNewCart" class="only-desktop" />

                <cart-gamification
                  v-else
                  :is-promotion="isBuyTwoGetFour"
                  class="only-desktop"
                />

                <coupon-code
                  v-if="!useNewCart"
                  :applied-promotion="cart.applied_promotion"
                  class="only-mobile"
                  @updateCart="updateCart"
                />
                <br class="only-mobile" />

                <div class="cart__totals">
                  <cart-summary
                    v-if="useNewCart"
                    :summary="cartSummary"
                    :promotion-applied="promotionApplied"
                    :promotions-summary="promotionsSummary"
                    :loading-shipping="loadingShipping"
                    :free-shipping="isFreeShipping"
                    @updateCart="updateCart"
                  >
                  </cart-summary>

                  <line-item-summary
                    v-else-if="!useNewCart"
                    :summary="summary"
                    :loading-shipping="loadingShipping"
                    :free-shipping="isFreeShipping"
                    @updateCart="updateCart"
                  />

                  <coupon-code
                    v-if="!useNewCart"
                    :applied-promotion="cart.applied_promotion"
                    class="only-desktop"
                    @updateCart="updateCart"
                  />

                  <v-button
                    name="checkout"
                    :label="$t('cart.cart_checkout')"
                    type="submit"
                    :class="[
                      'btn',
                      'btn--large',
                      'btn--success',
                      'cart__checkout-btn',
                      { 'only-desktop': storeInfo.store === 'br' },
                    ]"
                    @click="goToCheckout"
                  />

                  <div v-if="evaluatedExperiment">
                    <nuxt-link to="/" class="cart-desktop-products-link">
                      <p class="cart-desktop-products-text only-desktop">
                        {{ $t('choose_more_products') }}
                      </p>
                    </nuxt-link>
                  </div>

                  <paypal-checkout
                    v-if="showPaypal"
                    :order-number="cartNumber"
                  />
                  <apple-pay-checkout
                    v-if="showApplePay"
                    :order-number="cartNumber"
                    :cart-total="cart.total"
                    :items-total="cart.item_total"
                    :publishable-key="applePayPublishableKey"
                    @showLoading="showLoading"
                  />

                  <div v-if="showPaypal" class="other-payments-instruction">
                    {{ $t('cart.paypal_code_instructions') }}
                  </div>

                  <cart-mobile-finalize-btn
                    v-if="storeInfo.store === 'br' && !evaluatedExperiment"
                    class="only-mobile"
                    :summary="summary"
                    :cart="cart"
                  />

                  <new-cart-mobile-finalize-btn
                    v-if="storeInfo.store === 'br' && evaluatedExperiment"
                    class="only-mobile"
                    :summary="summary"
                    :cart="cart"
                  />
                </div>

                <transition name="fade">
                  <global-cart-free-shipping-message
                    v-if="showFreeShippingMessage"
                  />
                </transition>

                <div class="only-mobile cart__line-divisor"></div>
                <shipping-estimate
                  v-if="storeInfo.store === 'br'"
                  :cart-number="cartNumber"
                />
                <div
                  v-if="storeInfo.store === 'br'"
                  class="only-mobile cart__line-divisor"
                ></div>
              </div>
            </div>
            <p class="only-mobile center cart__keep-shopping">
              {{ $t('cart.not_finish') }}
              <nuxt-link to="/" class="inline-link continue">
                {{ $t('cart.keep_buying') }}
              </nuxt-link>
            </p>
            <div class="only-mobile cart__line-divisor"></div>
          </template>
        </div>
        <shelf
          :shelf-products="{
            type: 'rich_relevance',
            placement_name: 'cart_page.related_to_cart',
          }"
          :line-items="lineItems"
          :class="'shelf--cart shelf--grey'"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import CartSummary from '~/components/cart/cart-summary/CartSummary.vue'
import LogoLoading from '~/components/loadings/logo'
import LineItem from '~/components/line-item/LineItem.vue'
import CouponCode from '~/components/cart/coupon-code/CouponCode'
import CartGamification from '~/components/cart/cart-gamification/CartGamification'
import LineItemSummary from '~/components/line-item/line-item-summary/LineItemSummary.vue'
import ShippingEstimate from '~/components/cart/shipping-estimate/ShippingEstimate'
import CartMobileFinalizeBtn from '~/components/cart/cart-mobile-finalize-btn/CartMobileFinalizeBtn.vue'
import NewCartMobileFinalizeBtn from '~/components/cart/new-cart-mobile-finalize-btn/NewCartMobileFinalizeBtn.vue'
import PaypalCheckout from '~/components/cart/paypal-checkout/PaypalCheckout'
import ApplePayCheckout from '~/components/cart/apple-pay-checkout/ApplePayCheckout'
import VButton from '~/components/v-button/VButton.vue'
import Shelf from '~/components/pages/shelf/Shelf.vue'
import NewCrossSell from '~/components/cross-sell/NewCrossSell'
import urlCouponApplier from '~/mixins/url-coupon-applier'
import checkoutable from '~/mixins/checkoutable'
import GlobalCartFreeShippingMessage from '~/components/cart/global-cart-free-shipping-message/GlobalCartFreeShippingMessage.vue'
import isBlank from '~/utils/isBlank'
import Ga4Parser from '~/utils/Ga4Parser'
import IconWarning from '~/components/icons/warning/Warning.vue'
import ThreeDotsLoading from '~/components/loadings/three-dots'
import growthBookMixin from '~/mixins/growthbook/growthBookMixin'

export default {
  name: 'Cart',
  components: {
    LineItem,
    LogoLoading,
    CouponCode,
    LineItemSummary,
    CartGamification,
    ShippingEstimate,
    CartMobileFinalizeBtn,
    NewCartMobileFinalizeBtn,
    PaypalCheckout,
    ApplePayCheckout,
    VButton,
    NewCrossSell,
    Shelf,
    GlobalCartFreeShippingMessage,
    CartSummary,
    IconWarning,
    ThreeDotsLoading,
  },
  mixins: [urlCouponApplier, checkoutable, growthBookMixin],
  data() {
    return {
      cart: null,
      isLoading: true,
      showCheckboxes: true,
      checkboxLoading: true,
      loadingShipping: false,
      activeApplePay: false,
      applePayPublishableKey: null,
      activeNewCart: true,
    }
  },
  computed: {
    showPaypal() {
      return false // this.$isGlobalStore && this.cart.valid_to_paypal
    },
    showApplePay() {
      return (
        // eslint-disable-next-line camelcase
        this.$isGlobalStore &&
        this.cart.item_total &&
        this.activeApplePay &&
        this.cart.valid_to_paypal
      )
      // return this.$isGlobalStore && this.cart.total && this.cart.shipment_total
    },
    showFreeShippingMessage() {
      return this.$isGlobalStore && this.isFreeShipping
    },
    isFreeShipping() {
      return [
        0,
        '0.0',
        'Free',
        'Grátis',
        'Kostenlos',
        'Gratis',
        'Gratuit',
      ].includes(this.cart.shipment_total)
    },
    storeInfo() {
      return this.$store.getters.storeInfo
    },
    number() {
      return this.$store.state.orderNumber
    },
    cartNumber() {
      return this.number || this.cart?.number
    },
    lineItems() {
      return this.cart ? this.cart.line_items : null
    },
    summary() {
      if (!this.cart) return
      if (
        !this.cart.item_percentage_promotion_info ||
        Object.keys(this.cart.item_percentage_promotion_info).length === 0
      ) {
        return {
          itemTotal: this.cart.item_total,
          shipmentTotal: this.cart.shipment_total,
          total: this.cart.total,
          adjustmentTotal: this.cart.adjustment_total,
          promotionItems: [],
        }
      } else {
        const filteredAdjustments = this.cart.adjustment_total.filter(
          (item) =>
            item.label !==
            this.cart.item_percentage_promotion_info.promotion.label
        )

        return {
          shipmentTotal: this.cart.shipment_total,
          total: this.cart.total,
          promotionDiscount: this.cart.item_percentage_promotion_info.promotion,
          promotionItems: this.cart.item_percentage_promotion_info.items,
          adjustmentTotal: filteredAdjustments,
        }
      }
    },
    isBuyTwoGetFour() {
      if (!this.cart) return

      return (
        this.cart.item_percentage_promotion_info &&
        Object.keys(this.cart.item_percentage_promotion_info).length !== 0
      )
    },
    promotionApplied() {
      if (!this.cart) {
        return
      }
      if (
        // eslint-disable-next-line camelcase
        this.cart.item_percentage_promotion_info &&
        Object.keys(this.cart.item_percentage_promotion_info).length !== 0
      ) {
        // eslint-disable-next-line camelcase
        return this.cart.eligible_promotion_cheapest_info?.promotion?.[0]
      } else {
        // eslint-disable-next-line camelcase
        return this.cart.eligible_promotion_cheapest_info?.promotion?.[1]
      }
    },
    promotionsSummary() {
      if (!this.cart) return
      if (
        Object.keys(this.cart.eligible_promotion_cheapest_info || {}).length !==
          0 &&
        Object.keys(this.cart.eligible_promotion_cheapest_info || {}).length !==
          0
      ) {
        return this.cart.eligible_promotion_cheapest_info
      } else {
        return {}
      }
    },
    cartSummary() {
      if (!this.cart) return
      if (!this.isBuyTwoGetFour) {
        const adjustmentDiscount = this.cart.adjustment_total.reduce(
          (total, item) => total + parseFloat(item.total),
          0
        )

        return {
          lineItems: this.cart.line_items,
          itemTotal: this.cart.item_total,
          shipmentTotal: this.cart.shipment_total,
          total: this.cart.total,
          adjustmentTotal: this.cart.adjustment_total,
          promotionItems: [],
          adjustmentDiscount,
          appliedPromotion: this.cart.applied_promotion,
        }
      } else {
        const filteredAdjustments = this.cart.adjustment_total.filter(
          (item) =>
            item.label !==
            this.cart.item_percentage_promotion_info.promotion.label
        )

        const adjustmentDiscount = this.cart.item_percentage_promotion_info
          .promotion.total

        return {
          lineItems: this.cart.line_items,
          shipmentTotal: this.cart.shipment_total,
          total: this.cart.total,
          promotionDiscount: this.cart.item_percentage_promotion_info.promotion,
          promotionItems: this.cart.item_percentage_promotion_info.items,
          adjustmentTotal: filteredAdjustments,
          adjustmentDiscount,
          appliedPromotion: this.cart.applied_promotion,
        }
      }
    },
    useNewCart() {
      return this.$isBrStore && this.activeNewCart
    },
    isEmpty() {
      return (this.lineItems || [])?.length === 0
    },
    cartToGtm() {
      if (this.isEmpty) {
        return this.cart
      } else {
        const gtmCart = this.cart
        // eslint-disable-next-line camelcase
        gtmCart.line_items = gtmCart.line_items.map((lineItem) => {
          lineItem.product_url = `${window.location.origin}/${lineItem.slug}/p`

          return lineItem
        })

        return gtmCart
      }
    },
    showTravelCaseWarning() {
      if (this.isEmpty) return false

      const travelCaseItems = this.cart.line_items.filter(
        (item) => item.product_type_code === 'travelcasecd'
      )

      const quantity = travelCaseItems.reduce(
        (acc, item) => acc + item.quantity,
        0
      )

      return quantity > 2
    },
  },
  async mounted() {
    await this.initializeGrowthBook('newCartPage')

    this.$store.dispatch(
      'growthbook/updateEvaluatedExperiment',
      this.evaluatedExperiment
    )

    // deactivate loadapplepay call when not used
    await Promise.all([this.fetchMethod(), this.loadApplePay()])
    // await this.fetchMethod()

    // eslint-disable-next-line camelcase
    if (this.cart?.line_items?.length) {
      const ga4Items = new Ga4Parser(this.$store.state).viewCart(
        this.cart.line_items
      )

      const ecommerce = new Ga4Parser(this.$store.state).viewCartV2(
        this.cart.line_items,
        this.cart
      )

      /* 
        Prevent Side Effect since ga4EventValue is an array, 
        gtm automaticly append a new item instead of create a new array  
      */
      this.pushDataLayer({
        ga4EventValue: undefined,
      })

      this.pushDataLayer({
        event: 'genericEvent',
        eventCategory: 'navigation',
        eventAction: 'viewCart',
        eventValue: { ...this.cartToGtm, ...this.$store.getters.sessionInfo },
        ecommerce,
        ga4EventValue: ga4Items,
      })
    }
  },
  methods: {
    async loadApplePay() {
      try {
        const {
          active,
          shazam_in_store: shazam,
          publishable_key: publishableKey,
        } = await this.$apiv2.$get('/stripe_buttons/info', {
          params: {
            store: this.$store.state.storeCode,
            token: this.$config.spreeToken,
            substore: this.$store.state.substore?.code,
          },
        })
        this.activeApplePay = active && shazam
        this.applePayPublishableKey = publishableKey
      } catch (err) {
        this.$sentry.captureException(err?.response?.data?.message || err)
      }
    },
    setGlobalShipping() {
      if (this.$isBrStore || isBlank(this.cart) || this.cart.shipment_total)
        return
      this.loadingShipping = true
      this.fetchShippingEstimate()
        .then((response) => {
          this.cart.shipment_total = response.display_ship_total
          if (response.display_total) this.cart.total = response.display_total
        })
        .finally(() => {
          this.loadingShipping = false
        })
    },
    showLoading() {
      this.isLoading = true
    },
    async fetchShippingEstimate() {
      try {
        const { storeInfo, number, cartNumber } = this
        const params = { ...storeInfo, number: number || cartNumber }
        const shippingEstimate = await this.$axios.$get(
          `/shipping_estimates/estimate_total`,
          { params }
        )

        return shippingEstimate
      } catch (err) {
        const fallbackResponse = {
          display_ship_total: null,
        }

        return fallbackResponse
      } finally {
        this.isLoading = false
      }
    },
    updateCart(cart) {
      this.cart = cart
      this.setGlobalShipping()
      this.$store.dispatch('gamification/updateGamification', {
        orderNumber: this.$store.state.orderNumber,
        storeInfo: this.$store.getters.storeInfo,
      })
    },
    async updatedLineItem() {
      await this.fetchMethod()
    },
    async fetchMethod() {
      try {
        const { storeInfo, number } = this
        const params = { ...storeInfo, number }

        const getCart = await this.$axios.$get(`/cart`, { params })
        this.cart = getCart.cart
        this.$store.dispatch('gamification/updateGamification', {
          orderNumber: this.$store.state.orderNumber,
          storeInfo: this.$store.getters.storeInfo,
        })

        if (this.$isGlobalStore) {
          this.setGlobalShipping()
        }
      } catch (err) {
        this.cart = null
        this.$displayTopMessage(this.$t('action_error'), {
          type: 'error',
        })
      } finally {
        this.isLoading = false
      }
    },
    showCheckboxesHandler(shouldShow) {
      this.showCheckboxes = shouldShow
      if (shouldShow) {
        this.checkboxLoading = true
        clearTimeout(this.checkboxTimeout)
        this.checkboxTimeout = setTimeout(() => {
          this.checkboxLoading = false
        }, 500)
      }
    },
  },
  head() {
    return {
      title: this.$t('cart.page_title'),
    }
  },
}
</script>

<style lang="scss">
@import '~/assets/css/_content.scss';
@import '~/assets/css/grid.scss';
@import '~/assets/css/form.scss';
@import '~/assets/css/btn.scss';

.cart__line-divisor {
  background-color: $color_gray;
  height: 10px;
  margin: 0 -15px;
}

.cart__checkout-btn {
  width: 90%;
  margin: 0 auto;
}

.cart__header {
  display: none;

  .cart__title {
    font-size: 28px;
    font-weight: bold;
    margin: 0;
    line-height: normal;
  }

  .cart__subtitle {
    font-size: 14px;
    margin: 0 0 15px;
  }
}

.cart__totals {
  padding: 0;
  margin: 0 -15px;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  border-top: 1px solid $color_gray;
  padding-top: 5px;
}

.cart__items {
  margin: 0 -15px 0;

  .floating-label {
    margin-top: 5px;
  }
}

.cart__empty {
  text-align: center;
}
.cart__empty p {
  font-size: 15px;
}

.cart__keep-shopping {
  font-size: 15px;
  margin: 20px 0;
  .continue {
    display: block;
  }
}

.cart-desktop-products-link {
  color: inherit;
}

.cart-desktop-products-text {
  text-decoration: underline;
  text-align: center;
}

.travelcase-warning {
  border-radius: 4px;
  padding: 10px 14px;
  background: $v4_color_primary_lighter;
  margin-bottom: 20px;
}

.travelcase-warning__title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  margin-bottom: 5px;
  text-align: center;
}

.travelcase-warning__description {
  margin: 0;
  text-align: center;
  font-style: italic;
  font-size: 13px;
}

.support-link {
  color: #f37053;
  text-decoration: underline;
}

@media (max-width: $screen_tablet) {
  .cart__order {
    display: flex;
    flex-wrap: wrap;

    .cart__order-first {
      order: 1;
      width: 100%;
    }
    .cart__order-last {
      width: 100%;
      order: 2;
    }
  }

  .travelcase-warning {
    padding: 10px 15px;
  }

  .travelcase-warning__title {
    flex-direction: column;
    gap: 10px;
  }
}

@media (min-width: $screen_tablet) {
  .cart__items {
    margin: 0;
  }
}

@media (min-width: $screen_large) {
  .cart__order-last {
    margin-bottom: 60px;
  }

  .cart__header {
    display: block;
  }

  .cart__totals {
    background: #f6f6fa;
    margin: 0 0 20px;
    display: flex;
    padding: 12px 0 20px;
    margin: 0;
    border-top: 0;
    justify-content: center;
  }

  .cart__keep-shopping {
    margin: 15px 0;
  }

  .cart__checkout-btn {
    margin-top: 20px;
  }
}

// overwrite
@media (max-width: $screen_tablet) {
  .content {
    padding-top: 0 !important;
  }

  .line-item-summary {
    margin: 10px 0px !important;
  }
}

.center-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;
}

.cart__loading {
  background-color: #f6f6fa;
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cart__payment-options {
  width: 100%;
  display: block;
  padding: 0 15px;
  margin: 0 auto;
  min-height: 50px;
}

.other-payments-instruction {
  margin: 0 auto;
  margin-top: 5px;
  padding: 0px 15px;
  text-align: center;
}

@media (max-width: $v4_tablet) {
  .other-payments-instruction {
    margin: 15px auto;
  }
}
</style>
